import { ShoppingCartProducts } from '../store/reducers/CartReducer';
import { GiftProductNode, ProductNode } from '../interfaces/contentful';
import { getDistanceFromLudsLocationTo } from '../geo/geoHelpers';

export const calculateAmount = (product: (ProductNode | GiftProductNode) & { quantity: number }): number => {
  return (product.price * 100 * product.quantity) / 100;
};

export const calculateTotalAmount = (products: ShoppingCartProducts): number => {
  return (
    Object.values(products).reduce((acc, product) => {
      const price = product.price;
      return acc + price * 100 * product.quantity;
    }, 0) / 100
  );
};

export const calculateTotalAmountForGifts = (products: GiftProductNode[]): number => {
  return (
    Object.values(products).reduce((acc, product) => {
      const price = product.price;
      return acc + price * 100;
    }, 0) / 100
  );
};

/**
 * If you ever need to change the delivery price algorithm, remember to do it also in Lambda
 */
export const calculateTotalDeliveryAmount = (products: ShoppingCartProducts): number => {
  const FIXED_DELIVERY_PRICE_IN_CENTS = 250;
  const DYNAMIC_DELIVERY_PRICE_PERCENTAGE_OF_TOTAL_ORDER_PRICE = 0.05;

  const distances: number[] = [];
  Object.values(products).forEach((product) => {
    const distance = getDistanceFromLudsLocationTo(product.suppliedBy.location);
    distances.push(distance);
  });

  const totalAmountInCents = calculateTotalAmount(products) * 100;

  // If someone orders only one type of product (so 4 x vanilla yoghurt is fine), give him the distance based delivery price discount
  if (distances.length === 1) {
    let LOCATION_BASED_PRICE_IN_CENTS = FIXED_DELIVERY_PRICE_IN_CENTS;

    if (distances[0] < 5) {
      LOCATION_BASED_PRICE_IN_CENTS = 50; // 0.50 EUR if product within 5 km from LUDS
    } else if (distances[0] < 10) {
      LOCATION_BASED_PRICE_IN_CENTS = 100; // 1.00 EUR if product between 5km-10km from LUDS
    } else if (distances[0] < 15) {
      LOCATION_BASED_PRICE_IN_CENTS = 200; // 2.00 EUR if product between 10km - 15km from LUDS
    }

    const distanceBasedDeliveryPrice =
      LOCATION_BASED_PRICE_IN_CENTS + totalAmountInCents * DYNAMIC_DELIVERY_PRICE_PERCENTAGE_OF_TOTAL_ORDER_PRICE;

    return distanceBasedDeliveryPrice / 100;
  }

  return (
    (FIXED_DELIVERY_PRICE_IN_CENTS + totalAmountInCents * DYNAMIC_DELIVERY_PRICE_PERCENTAGE_OF_TOTAL_ORDER_PRICE) / 100
  );
};

export const calculateTotalAmountWithDelivery = (products: ShoppingCartProducts): number => {
  const totalAmountInCents = calculateTotalAmount(products) * 100;
  const deliveryAmountInCents = calculateTotalDeliveryAmount(products) * 100;

  return (totalAmountInCents + deliveryAmountInCents) / 100;
};

// Not needed to change lambda because this will be used only on the client side via mailto functionality
export const calculateTotalAmountWithDeliveryDiscount = (
  products: ShoppingCartProducts,
  deliveryDiscount: number,
): number => {
  const totalAmountInCents = calculateTotalAmount(products) * 100;
  const deliveryAmountInCents = deliveryDiscount * 100;

  return (totalAmountInCents + deliveryAmountInCents) / 100;
};

export const calculateTotalAmountWithDeliveryForGifts = (
  chosenPackage: GiftProductNode,
  products: GiftProductNode[],
  postalCode: string,
  numberOfKados = 1,
): number => {
  const chosenPackageAmountInCents = chosenPackage.price * 100;
  const totalAmountInCents = calculateTotalAmountForGifts(products) * 100;
  const deliveryAmountInCents = (calculateTotalDeliveryAmountPerRegion(postalCode) as number) * 100;

  return ((chosenPackageAmountInCents + totalAmountInCents + deliveryAmountInCents) * numberOfKados) / 100;
};

export const calculateTotalDeliveryAmountPerRegion = (postalCode: string, checkOnly = false): number | boolean => {
  let deliveryCost = 0;

  const redRegionPostalCodes = [6701, 6702, 6703, 6707, 6706, 6708, 6709, 6704, 6705, 6871];
  const orangeRegionPostalCodes = [
    3921,
    3922,
    3901,
    3902,
    3907,
    3903,
    3904,
    3905,
    3906,
    6721,
    6718,
    6745,
    6716,
    6717,
    6711,
    6712,
    6713,
    6714,
    6715,
    3911,
    3912,
    6871,
    6866,
    6874,
  ];

  const purpleRegionPostalCodes = [6668, 6666, 6665, 4043, 4041, 6865, 6861, 6862, 6869, 4033, 4031, 4032];

  const cleanedPostalCode = Number(postalCode.trim().substring(0, 4));
  console.log('Calculating POSTAL CODE', postalCode, cleanedPostalCode);

  if (redRegionPostalCodes.includes(cleanedPostalCode)) {
    console.log(`Code ${cleanedPostalCode} belong to RED`);
    deliveryCost = 5;
  } else if (orangeRegionPostalCodes.includes(cleanedPostalCode)) {
    console.log(`Code ${cleanedPostalCode} belong to ORANGE`);
    deliveryCost = 7.5;
  } else if (purpleRegionPostalCodes.includes(cleanedPostalCode)) {
    console.log(`Code ${cleanedPostalCode} belong to PURPLE`);
    deliveryCost = 10;
  } else {
    console.error(`Code ${cleanedPostalCode} does not belong anywwhere, NO delivery`);
    deliveryCost = 0;
  }

  return checkOnly ? Boolean(deliveryCost) : deliveryCost;
};
